import { useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TAB_ORDER, resolveTabPath } from '@/lib/tabRoutes';

function shouldIgnoreTouch(el, boundary) {
  let node = el;
  while (node && node !== boundary) {
    if (node.dataset && node.dataset.noSwipe !== undefined) return true;
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'VIDEO' || tag === 'SELECT') return true;
    try {
      const style = window.getComputedStyle(node);
      const ox = style.overflowX;
      if ((ox === 'auto' || ox === 'scroll') && node.scrollWidth > node.clientWidth + 5) return true;
    } catch {}
    node = node.parentElement;
  }
  return false;
}

export function SwipeNavigator({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const touch = useRef(null);
  const prevIdx = useRef(TAB_ORDER.indexOf(resolveTabPath(location.pathname)));

  const idx = TAB_ORDER.indexOf(resolveTabPath(location.pathname));
  const enabled = idx !== -1;

  let dir = 0;
  if (idx !== -1 && prevIdx.current !== -1 && idx !== prevIdx.current) {
    dir = idx > prevIdx.current ? 1 : -1;
  }
  useEffect(() => {
    if (idx !== -1) prevIdx.current = idx;
  });

  const onTouchStart = (e) => {
    if (!enabled || e.touches.length !== 1) { touch.current = null; return; }
    if (shouldIgnoreTouch(e.target, containerRef.current)) { touch.current = null; return; }
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const onTouchEnd = (e) => {
    if (!enabled || !touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    const dt = Date.now() - touch.current.time;
    touch.current = null;
    if (dt > 600) return;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const next = dx < 0 ? idx + 1 : idx - 1;
    if (next < 0 || next >= TAB_ORDER.length) return;
    navigate(TAB_ORDER[next]);
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-swipe-container
      data-testid="swipe-navigator"
    >
      <motion.div
        key={enabled ? location.pathname : 'static'}
        initial={dir !== 0 ? { x: dir * 48, opacity: 0.4 } : false}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </div>
  );
}
